import type { FC } from "react";
import { OrderStatus } from "../../types/order/IOrder";
import { useTranslation } from "react-i18next";
import {
  badgeBase,
  cn,
  statusCancelled,
  statusCompleted,
  statusDelivery,
  statusDraft,
  statusDotCancelled,
  statusDotCompleted,
  statusDotDelivery,
  statusDotDraft,
  statusDotInProgress,
  statusDotNew,
  statusDotPickup,
  statusDotPickupDelivery,
  statusDotScheduled,
  statusInProgress,
  statusNew,
  statusPickup,
  statusPickupDelivery,
  statusScheduled,
} from "@design-system";

interface StatusBadgeProps {
  status: OrderStatus | string;
  bgStyle?: string;
  dot?: boolean;
  dotStyle?: string;
  rounded?: boolean;
}

export const StatusBadge: FC<StatusBadgeProps> = function (
  props: StatusBadgeProps,
) {
  const { t } = useTranslation(["stats", "common"]);

  const getStatusAndStyle = () => {
    switch (props.status) {
      case OrderStatus.Draft:
        return {
          text: t("stats:order_status.draft"),
          bgStyle: statusDraft,
          dotStyle: statusDotDraft,
        };
      case OrderStatus.New:
        return {
          text: t("stats:order_status.new"),
          bgStyle: statusNew,
          dotStyle: statusDotNew,
        };
      case OrderStatus.Scheduled:
        return {
          text: t("stats:order_status.scheduled"),
          bgStyle: statusScheduled,
          dotStyle: statusDotScheduled,
        };
      case OrderStatus.InProgress:
        return {
          text: t("stats:order_status.in_progress"),
          bgStyle: statusInProgress,
          dotStyle: statusDotInProgress,
        };
      case OrderStatus.Cancelled:
        return {
          text: t("stats:order_status.cancelled"),
          bgStyle: statusCancelled,
          dotStyle: statusDotCancelled,
        };
      case OrderStatus.Completed:
        return {
          text: t("stats:order_status.completed"),
          bgStyle: statusCompleted,
          dotStyle: statusDotCompleted,
        };
      case t("common:pickup"):
        return {
          text: t("common:pickup"),
          bgStyle: statusPickup,
          dotStyle: statusDotPickup,
        };
      case t("common:delivery"):
        return {
          text: t("common:delivery"),
          bgStyle: statusDelivery,
          dotStyle: statusDotDelivery,
        };
      case t("common:pickup_and_delivery"):
        return {
          text: t("common:pickup_and_delivery"),
          bgStyle: statusPickupDelivery,
          dotStyle: statusDotPickupDelivery,
        };
      default:
        return {
          text: props.status,
          bgStyle: props.bgStyle ? props.bgStyle + " " : statusDraft,
          dotStyle: props.dotStyle ? props.dotStyle : statusDotDraft,
        };
    }
  };

  return (
    <div
      className={cn(
        badgeBase,
        getStatusAndStyle().bgStyle,
        props.rounded ? "rounded-full" : "rounded-md",
      )}
    >
      {props.dot && (
        <div
          className={"rounded " + getStatusAndStyle().dotStyle}
          style={{ width: "8px", height: "8px" }}
        ></div>
      )}
      <span>{getStatusAndStyle().text}</span>
    </div>
  );
};
